import * as React from 'react';
import { useContext } from 'react';
import { Callout, DirectionalHint, Icon, List } from '@fluentui/react';
import { ILayer } from '../../../../models';
import styles from './LeftMenu.module.scss';
import { getLayerKey, ILayerRuntimeStatus, LayerHealthStatus } from '../LayerHealth/LayerHealthTypes';
import { LanguageContext } from '../../../../LanguageContext';

interface ILeftMenuProps {
  layers: ILayer[];
  backgroundLayers: ILayer[];
  backgroundLayer: string;
  layerStatusByKey: { [key: string]: ILayerRuntimeStatus };
  layerStatusRevision: number;
  layerStatusCycle: number;
  onLayerItemClick(index: number): void;
  onBackgroundLayerClick(item?: ILayer): void;
  onLayerSignatureClick(index: number): void;
}

const LeftMenu: React.FC<ILeftMenuProps> = ({
  layers,
  backgroundLayers,
  backgroundLayer,
  layerStatusByKey,
  layerStatusRevision,
  layerStatusCycle,
  onLayerItemClick,
  onBackgroundLayerClick,
  onLayerSignatureClick
}) => {
  const languageContext = useContext(LanguageContext);
  const t = (key: string): string => languageContext?.t ? languageContext.t(key) : key;

  const getLayerStatus = (layer?: ILayer): ILayerRuntimeStatus => {
    const key = getLayerKey(layer);

    return layerStatusByKey[key] || { status: 'unknown', lastUpdated: 0, cycle: layerStatusCycle };
  };

  const getLayerStatusIconName = (status: LayerHealthStatus): string => {
    switch (status) {
      case 'warning':
        return 'WarningSolid';
      case 'ok':
        return 'CompletedSolid';
      default:
        return 'StatusCircleRing';
    }
  };

  const onRenderLayerListItem = (item?: ILayer, index?: number): JSX.Element => {
    const isActive = item?.enabled;
    const layerStatus = getLayerStatus(item);
    const statusIconName = getLayerStatusIconName(layerStatus.status);
    const statusClass = layerStatus.status === 'warning' ? styles.layerStatusWarning : layerStatus.status === 'ok' ? styles.layerStatusOk : styles.layerStatusUnknown;
    const statusTitle = layerStatus.status === 'warning'
      ? (layerStatus.lastError || t('leafletMap.layerStatusError'))
      : layerStatus.status === 'ok'
        ? t('leafletMap.layerStatusOk')
        : t('leafletMap.layerStatusPending');
    const displayStatusIconName = isActive ? statusIconName : getLayerStatusIconName('unknown');
    const displayStatusClass = isActive ? statusClass : styles.layerStatusUnknown;
    const displayStatusTitle = isActive ? statusTitle : t('leafletMap.layerHidden');

    return (
      <div className={styles.leftMenuItem} >
        <div className={styles.leftMenuItemRow} data-is-focusable={true} title={item?.name + ''} onClick={() => onLayerItemClick(index as number)}>
          <Icon iconName={displayStatusIconName} className={`${styles.layerStatusIcon} ${displayStatusClass}`} title={displayStatusTitle} />
          <Icon iconName={isActive ? 'RedEye' : 'Hide'} className={styles.layerVisibilityIcon} />
          <span>{item?.name + ''}</span>
        </div>
        {(item?.signature || item?.legendUrl) &&
          <div className={styles.leftMenuItemMore} onClick={() => onLayerSignatureClick(index as number)} >
            <Icon id={`hdmore_icon_${index}`} iconName={'More'} />
            {item?.showSignature &&
              <Callout
                target={`#hdmore_icon_${index}`}
                directionalHint={DirectionalHint.rightCenter}
                onDismiss={() => onLayerSignatureClick(index as number)}
                style={{maxHeight: '70vh', padding: 8}}
              >
                <img src={`/images/${item.signature}`} alt="" />
                <img className={styles.legendImage} src={item.legendUrl ? item.legendUrl : `/images/${item.signature}`} alt="" />
              </Callout>
            }
          </div>
        }
      </div>
    );
  };

  const onRenderBackgroundLayerListItem = (item?: ILayer): JSX.Element => {
    const isActive = backgroundLayer === item?.name;
    const layerStatus = getLayerStatus(item);
    const statusIconName = getLayerStatusIconName(layerStatus.status);
    const statusClass = layerStatus.status === 'warning' ? styles.layerStatusWarning : layerStatus.status === 'ok' ? styles.layerStatusOk : styles.layerStatusUnknown;
    const statusTitle = layerStatus.status === 'warning'
      ? (layerStatus.lastError || t('leafletMap.layerStatusError'))
      : layerStatus.status === 'ok'
        ? t('leafletMap.layerStatusOk')
        : t('leafletMap.layerStatusPending');
    const displayStatusIconName = isActive ? statusIconName : getLayerStatusIconName('unknown');
    const displayStatusClass = isActive ? statusClass : styles.layerStatusUnknown;
    const displayStatusTitle = isActive ? statusTitle : t('leafletMap.layerHidden');

    return (
      <div className={styles.leftMenuItem} >
        <div className={styles.leftMenuItemRow} data-is-focusable={true} title={item?.name + ''} onClick={() => onBackgroundLayerClick(item)}>
          <Icon iconName={displayStatusIconName} className={`${styles.layerStatusIcon} ${displayStatusClass}`} title={displayStatusTitle} />
          <Icon iconName={isActive ? 'RedEye' : 'Hide'} className={styles.layerVisibilityIcon} />
          <span>{item?.name + ''}</span>
        </div>
      </div>
    );
  };

  return <div className={styles.leftMenu}>
    <div className={styles.leftMenuHeading}>{t('leafletMap.mapLayers')}</div>
    <List key={`overlay-status-${layerStatusRevision}`} items={layers} onRenderCell={(item?: ILayer, index?: number) => { return onRenderLayerListItem(item, index); }} />
    <div className={styles.leftMenuDivider}></div>
    <div className={styles.leftMenuHeading}>{t('leafletMap.backgroundMaps')}</div>
    <List key={`background-status-${layerStatusRevision}`} items={backgroundLayers} onRenderCell={(item?: ILayer) => { return onRenderBackgroundLayerListItem(item); }} />
  </div>
}

export default LeftMenu;
